/**
 * Browser storage of the encrypted wallet file for the wallet pages.
 *
 * One wallet file (wallet.json, the CLI's layout) is kept in IndexedDB on this origin. Only the sealed
 * file is stored: the mnemonic exists in clear only inside the page session after the password is
 * entered. The page asks the browser for persistent storage and tells the user to download the file
 * when the browser refuses, because the browser may evict site data and a private window discards it.
 *
 * Browser code: IndexedDB and the Storage API only, no Node imports.
 */

import type { WalletData } from 'streembit-bc-sdk';

const DB_NAME = 'streembit-wallet';
const DB_VERSION = 1;
const STORE = 'wallets';
const KEY = 'default';

export interface StoredWallet {
    file: WalletData;
    /** account 0, address 0 of the wallet, kept so the Wallet tab can show it before the wallet is unlocked */
    address: string;
    savedAt: string;
}

function openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        if (!('indexedDB' in globalThis)) {
            reject(new Error('This browser has no IndexedDB; the wallet cannot be kept in the browser'));
            return;
        }
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            if (!req.result.objectStoreNames.contains(STORE)) {
                req.result.createObjectStore(STORE);
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('IndexedDB could not be opened'));
        req.onblocked = () => reject(new Error('IndexedDB is blocked by another tab'));
    });
}

function transaction<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    return openDatabase().then(db => new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const req = run(tx.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
        tx.oncomplete = () => db.close();
        tx.onabort = () => { db.close(); reject(tx.error ?? new Error('IndexedDB transaction aborted')); };
    }));
}

/** The stored wallet, or null when none is kept in this browser or storage is unavailable. */
export async function loadStoredWallet(): Promise<StoredWallet | null> {
    try {
        const value = await transaction<unknown>('readonly', store => store.get(KEY));
        if (!value || typeof value !== 'object') return null;
        const stored = value as StoredWallet;
        if (!stored.file || typeof stored.file !== 'object' || typeof stored.address !== 'string') return null;
        return stored;
    } catch {
        return null;
    }
}

export function saveStoredWallet(file: WalletData, address: string): Promise<void> {
    const record: StoredWallet = { file, address, savedAt: new Date().toISOString() };
    return transaction('readwrite', store => store.put(record, KEY)).then(() => undefined);
}

export function deleteStoredWallet(): Promise<void> {
    return transaction('readwrite', store => store.delete(KEY)).then(() => undefined);
}

/** Ask the browser to protect this origin's storage from eviction. False when refused or unsupported. */
export async function requestPersistence(): Promise<boolean> {
    try {
        if (!navigator.storage || !navigator.storage.persist) return false;
        if (navigator.storage.persisted && await navigator.storage.persisted()) return true;
        return await navigator.storage.persist();
    } catch {
        return false;
    }
}
